import { Helmet } from 'react-helmet-async';

export const SITE_NAME = 'Resida';
export const SITE_URL = 'https://www.resida.me';
const DEFAULT_DESCRIPTION =
  'Resida brings rent collection, lease management, maintenance tracking, and tenant communication into one platform built for landlords with 1 to 20 units.';

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  type?: 'website' | 'article';
  /** ISO dates, emitted as article:published_time / article:modified_time when type is 'article'. */
  publishedTime?: string;
  modifiedTime?: string;
  /** For pages that must not be indexed (the 404 page): adds robots noindex and drops the canonical. */
  noindex?: boolean;
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  type = 'website',
  publishedTime,
  modifiedTime,
  noindex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Rental Management for Independent Landlords`;
  const canonicalUrl = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex" /> : <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {!noindex && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content={SITE_NAME} />
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}
